import React, { useState } from 'react';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement } from 'react-reflex';

import { UserDetailAndAction } from '../userDetailAndAction';
import { Brand } from '../brand';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@quyva/shared-ui';
import {
    faCaretDown,
    faCaretLeft,
    faCaretUp,
    faFileAlt,
    faGear,
} from '@fortawesome/free-solid-svg-icons';
import { useGlobalCredits } from '../../context/GlobalStateContext';
import { Features, OtherFeatures, isFeatureEnabled } from '@quyva/common';
import { useFeatureFlags } from '../../context';
import { SettingSections } from '.';

const renderPromptList = (
    processes,
    onProcessClick,
    selectedProcess,
    formatProcessName
) => {
    return (
        <div className={`p-0 mt-0`}>
            {(processes || []).map((process) => {
                const isActive = selectedProcess === process.process_name;

                return (
                    <div
                        key={process.process_name}
                        className={`flex items-center h-8 truncate cursor-pointer border border-transparent hover:border hover:rounded-xl hover:border-backgrounds-primary300 hover:shadow-inner hover:bg-backgrounds-secondary
                                ${
                                    isActive
                                        ? ' bg-backgrounds-primary100 text-backgrounds-primary300'
                                        : ' hover:bg-backgrounds-primary100'
                                }`}
                        onClick={() => onProcessClick(process)}
                        title={formatProcessName(process.process_name)}
                    >
                        <span
                            className='mr-2 font-bold'
                            style={{ color: '#d4d4edff' }}
                        >
                            -
                        </span>
                        <FontAwesomeIcon
                            icon={faFileAlt} // prompt-like icon
                            color={isActive ? '#2563eb' : '#6b7280'}
                            className='mr-2 drop-shadow-sm'
                        />
                        <span className={`truncate text-base`}>
                            {formatProcessName(process.process_name)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export const SettingsIndex = ({
    settingSections,
    activeSection,
    setActiveSection,
    closeSidebar,
    processes,
    selectedProcess,
    setSelectedProcess,
    formatProcessName,
}) => {
    const { transactionMode, setTransactionMode } = useGlobalCredits();

    const BREAKPOINT_WIDTH = 1024;
    return settingSections.map((item, index) => (
        <div className='w-full text-texts-secondary300' key={index}>
            <button
                className={`w-full flex items-center justify-between ${
                    activeSection === item.name
                        ? 'bg-backgrounds-primary100 shadow-inner'
                        : ''
                } rounded-xl my-2 px-3 py-1 border border-transparent hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 text-texts-secondary300`}
                onClick={(event) => {
                    setActiveSection(item.name, item.ref);
                    if (transactionMode) {
                        setTransactionMode(!transactionMode);
                    }
                    event.stopPropagation();
                    if (window.innerWidth < BREAKPOINT_WIDTH) {
                        closeSidebar(); // Call the closeSidebar function passed from navbar.jsx
                    }
                }}
                title={item.hoverText}
            >
                <div className='flex-col w-full truncate'>
                    <div className='flex w-full items-center justify-between truncate max-w-prose'>
                        <div className='flex w-full items-center justify-start truncate max-w-prose'>
                            <FontAwesomeIcon
                                icon={item.icon}
                                fixedWidth
                                // color={'lightblue'}
                                className={`mr-2 drop-shadow-sm ${
                                    activeSection === item.name
                                        ? 'text-backgrounds-primary300'
                                        : 'text-icons-primary'
                                }`}
                            />
                            <div
                                className={`truncate text-base ${
                                    activeSection === item.name &&
                                    'text-backgrounds-primary300'
                                }`}
                            >
                                {item.name}
                            </div>
                        </div>
                        {item.name === SettingSections.PROMPT_SETTINGS && (
                            <FontAwesomeIcon
                                icon={
                                    activeSection ===
                                    SettingSections.PROMPT_SETTINGS
                                        ? faCaretUp
                                        : faCaretDown
                                }
                                className={`truncate ml-2 ${
                                    activeSection === item.name
                                        ? 'text-backgrounds-primary300'
                                        : ' text-icons-primary'
                                }`}
                                title={
                                    activeSection === item.name
                                        ? 'Expanded Menu'
                                        : 'More Items'
                                }
                            />
                        )}
                    </div>

                    {item.name === SettingSections.PROMPT_SETTINGS &&
                        activeSection === SettingSections.PROMPT_SETTINGS && (
                            <div className='w-full overflow-auto mt-1 pl-1 border-t'>
                                <div className='w-full text-texts-secondary300 border-l-2'>
                                    {/* Prompt list */}
                                    {renderPromptList(
                                        processes,
                                        (process) => {
                                            setSelectedProcess(
                                                process.process_name
                                            );
                                        },
                                        selectedProcess,
                                        formatProcessName
                                    )}
                                </div>
                            </div>
                        )}
                </div>
            </button>
        </div>
    ));
};

export const SettingsSideBar = ({
    settingSections,
    activeSection,
    setActiveSection,
    setIsLeftViewExpanded,
    isSidebarOpen,
    closeSidebar,
    setIsSidebarOpen,
    planName,
    processes,
    selectedProcess,
    setSelectedProcess,
    formatProcessName,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const BREAKPOINT_WIDTH = 1024;
    return (
        <div
            className={`z-0 h-full w-full transition-all duration-700 ease-in-out bg-transparent p-1 m-0 rounded-xl`}
        >
            <ReflexContainer orientation='horizontal'>
                <ReflexElement
                    className='header border-b-2 border-b-backgrounds-white'
                    style={{
                        minHeight: '8rem',
                        height: '8rem',
                        maxHeight: '8rem',
                    }}
                >
                    <div className='pane-content w-full h-full pb-2 px-1'>
                        <div className='w-full h-full flex flex-col justify-between items-center text-texts-secondary300'>
                            {isFeatureEnabled(
                                [Features.OTHERS, OtherFeatures.LOGO],
                                featureFlagsData
                            ) && <Brand />}

                            <div className='w-full flex items-center justify-between'>
                                <div className='w-full flex items-center truncate'>
                                    <FontAwesomeIcon
                                        icon={faGear}
                                        fixedWidth
                                        className='mr-2 drop-shadow-sm text-icons-primary'
                                    />
                                    <div className='font-semibold truncate text-base'>
                                        Setting Sections
                                    </div>
                                </div>
                                <div className='flex items-center justify-center'>
                                    <Button
                                        icon={faCaretLeft}
                                        className={'w-9 h-9'}
                                        onClick={() => {
                                            if (
                                                window.innerWidth >
                                                BREAKPOINT_WIDTH
                                            ) {
                                                // Adjust the breakpoint as per your design
                                                setIsLeftViewExpanded(false);
                                            } else {
                                                closeSidebar(); // Call the closeSidebar function passed from navbar.jsx
                                            }
                                        }}
                                        hoverText={
                                            'Collapse and hide repositories view'
                                        }
                                        type={'warning'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ReflexElement>

                <ReflexElement flex={1}>
                    <div className='pane-content p-1'>
                        <div className='h-full w-full overflow-auto'>
                            <SettingsIndex
                                settingSections={settingSections}
                                activeSection={activeSection}
                                setActiveSection={setActiveSection}
                                closeSidebar={closeSidebar}
                                processes={processes}
                                selectedProcess={selectedProcess}
                                setSelectedProcess={setSelectedProcess}
                                formatProcessName={formatProcessName}
                            />
                        </div>
                    </div>
                </ReflexElement>

                <ReflexElement
                    className='footer border-t-2 border-t-backgrounds-white flex flex-col justify-end'
                    style={{
                        minHeight: '7rem',
                        height: '7rem',
                        maxHeight: '7rem',
                        bottom: 5,
                        position:
                            window.innerWidth > BREAKPOINT_WIDTH
                                ? 'static'
                                : 'fixed',
                        width:
                            window.innerWidth > BREAKPOINT_WIDTH
                                ? 'auto'
                                : '100%',
                    }}
                >
                    <div className='pane-content w-full h-full'>
                        <UserDetailAndAction planName={planName} />
                    </div>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};
